/**
 * Reusable JSON-LD structured data schemas. Brand-specific values default
 * to West Slope Quiz (the fallback tenant) — pass the siteConfig brandName
 * to build them for another tenant. Geo/legal copy only renders for orgs
 * that keep the defaults, which is the correct fallback behavior.
 * See: https://schema.org/
 */

const DEFAULT_BRAND = 'West Slope Quiz';

export const localBusinessSchema = (brandName: string = DEFAULT_BRAND) => ({
  '@context': 'https://schema.org',
  '@type': 'EntertainmentBusiness',
  name: brandName,
  legalName: brandName === DEFAULT_BRAND ? 'West Slope Quiz Co LLC' : brandName,
  description:
    'Fun, fresh, and unforgettable pub trivia nights in Grand Junction and the Grand Valley. Weekly events at local venues, plus private parties, corporate events, and fundraisers.',
  url: 'https://www.westslopequiz.com',
  telephone: '+19706844311',
  email: 'contact@westslopequiz.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Grand Junction',
    addressRegion: 'CO',
    addressCountry: 'US',
  },
  areaServed: {
    '@type': 'GeoCircle',
    geoMidpoint: {
      '@type': 'GeoCoordinates',
      latitude: 39.0639,
      longitude: -108.5506,
    },
    geoRadius: '50000',
  },
  sameAs: [
    'https://www.instagram.com/west_slope_quiz_co/',
  ],
  founder: {
    '@type': 'Person',
    name: 'Ryan Fleming',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: `${brandName} Services`,
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Weekly Pub Trivia',
          description: 'Weekly trivia nights at local Grand Valley bars and restaurants',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Private Event Trivia',
          description:
            'Custom trivia events for corporate team building, birthday parties, and fundraisers',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Custom Branded Quizzes',
          description: 'Themed and branded trivia experiences designed for your venue or event',
        },
      },
    ],
  },
});

export const websiteSchema = (brandName: string = DEFAULT_BRAND) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: brandName,
  url: 'https://www.westslopequiz.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.westslopequiz.com/?s={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
});

/**
 * Generate an Event schema object from event data.
 * Pass in the processed event from the API.
 */
export function buildEventSchema(event: {
  name: string;
  calculatedDate: Date;
  venue: { name: string; link?: string | null };
  recurringTime?: string | null;
}, brandName: string = DEFAULT_BRAND) {
  const startDate = event.calculatedDate.toISOString();
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    startDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: event.venue.name,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Grand Junction',
        addressRegion: 'CO',
        addressCountry: 'US',
      },
    },
    organizer: {
      '@type': 'Organization',
      name: brandName,
      url: 'https://www.westslopequiz.com',
    },
    description: `Weekly pub trivia night at ${event.venue.name} hosted by ${brandName}.`,
    url: event.venue.link || 'https://www.westslopequiz.com',
  };
}
